import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import {
    BehaviorSubject,
    map,
    Subject,
    take,
    takeUntil,
    skip,
    Subscription
} from "rxjs";
import { defaultOrderBoardSearchParams } from "../../data-types/search-params";
import { OrdersService } from "../../services/orders.service";
import { ModalsService } from "../../shared/modals/modals.service";
import { StatusGroup } from "../../state";
import { Store } from "../../store";
import { GlobalSearchService } from "../global-search.service";
import { SearchActionsService } from "../search-actions.service";
import { SearchHistoryService } from "../search-history.service";

@Component({
    selector: "app-search-orders-view",
    templateUrl: "./search-orders-view.component.html",
    styleUrls: ["./search-orders-view.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchOrdersViewComponent implements OnInit, OnDestroy {
    params = { ...defaultOrderBoardSearchParams };
    destroy$ = new Subject<void>();
    isLoading$ = new BehaviorSubject(false);
    searchSubscription: Subscription;

    allStatusItem: StatusGroup = {
        name: "All status",
        id: -1,
    };

    statuses$ = this.store.select("statusGroups").pipe(
        map((data: any) => data?.data || []),
        map((items: StatusGroup[]) =>
            items.filter((item) => item.samplesOnly === 0)
        ),
        map((items: StatusGroup[]) => [
            this.allStatusItem,
            ...items,
        ])
    );
    selectedStatuses$ = new BehaviorSubject([this.allStatusItem.id]);

    orderLineGroups$ = new BehaviorSubject([]);
    hasEmptyResult$ = this.orderLineGroups$.pipe(
        map((data) => data.reduce((a, b) => a.concat(b?.items || []), [])),
        map((data) => !data.length)
    );

    constructor(
        private modalsService: ModalsService,
        private searchService: GlobalSearchService,
        private ordersService: OrdersService,
        private store: Store,
        private router: Router,
        private searchHistoryService: SearchHistoryService,
        private searchActionsService: SearchActionsService
    ) {}

    ngOnInit(): void {
        this.searchService.searchStored$
            .pipe(takeUntil(this.destroy$))
            .subscribe((keyword: string) => {
                this.params = { ...this.params, search: keyword };
                this.search();
            });
        this.selectedStatuses$
            .pipe(skip(1), takeUntil(this.destroy$))
            .subscribe((statuses) => {
                this.params = {
                    ...this.params,
                    groupIds: statuses.filter((itemId) => itemId !== -1),
                };
                this.search();
            });
    }

    orderModalComplete(e) {
        if (e.updated) {
        }
        this.modalsService.closeModal();
    }
    isStatusGroupSelected(statusGroup) {
        return this.selectedStatuses$.value.some(
            (itemId) => itemId === statusGroup.id
        );
    }
    selectStatusGroup(statusGroup: StatusGroup) {
        this.selectedStatuses$.pipe(take(1)).subscribe((selected) => {
            const selectedIdx = selected.findIndex(
                (itemId: number) => itemId === statusGroup.id
            );
            if (selectedIdx !== -1) {
                selected.splice(selectedIdx, 1);
            } else {
                if (statusGroup.id === -1) {
                    selected = [-1];
                } else {
                    // if empty clean "empty indicator" -1
                    if (selected.length === 1 && selected[0] === -1) {
                        selected = [];
                    }
                    selected.push(statusGroup.id);
                }
            }

            if(!selected.length) {
                selected.push(this.allStatusItem.id);
            }
            this.selectedStatuses$.next(selected);
        });
    }

    search() {
        if (!this.params.search?.trim()) {
            this.orderLineGroups$.next([]);
            this.isLoading$.next(false);
            return;
        }

        if(this.searchSubscription) {
            this.searchSubscription.unsubscribe();
            this.searchSubscription = null;
        }
        this.isLoading$.next(true);
        this.searchSubscription = this.ordersService
            .searchOrderLines({ ...this.params, noCache: true }, false)
            .subscribe({
                next: ({ data }: any) => {
                    this.orderLineGroups$.next(
                        data.groups.map(({ name, cols }) => {
                            return {
                                name,
                                items: cols.map((col) => col.orderLines).flat(),
                            };
                        })
                    );
                    this.isLoading$.next(false);
                },
                error: () => {
                    this.orderLineGroups$.next([]);
                    this.isLoading$.next(false);
                },
            });
    }

    goToOrder(orderLine: any) {
        this.searchHistoryService.addHistoryItem({
            data: orderLine,
            name: `ORD-${orderLine.publicId}/${orderLine.lineId}`,
            type: "order",
        });
        this.searchActionsService.goToOrder(orderLine);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }
}
